import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Download, Upload, Trash2, Pencil, Calculator, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const CARRIERS = ['Unlocked', 'Locked', 'ATT Locked'];
const STORAGES = ['ANY', '64GB', '128GB', '256GB', '512GB', '1TB', '2TB', '4TB', '8TB'];
const GRADES = ['Sealed', 'Opened', 'A', 'B/B+', 'B-', 'C', 'D', 'DOA'];

export default function Pricesheet() {
  const [data, setData] = useState({ grouped: [], models: [] });
  const [loading, setLoading] = useState(true);
  const [editCell, setEditCell] = useState(null); // {model, carrier, storage, grade, price}
  const [editValue, setEditValue] = useState('');
  const [addModelOpen, setAddModelOpen] = useState(false);
  const [newModelName, setNewModelName] = useState('');
  const [newModelCarriers, setNewModelCarriers] = useState(['Unlocked', 'Locked']);
  const [newModelStorages, setNewModelStorages] = useState(['256GB', '512GB', '1TB']);
  const fileInputRef = useRef(null);
  const wipeImportInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pricesheet`);
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load price sheet');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const savePrice = async (model, carrier, storage, grade, priceStr) => {
    const price = parseFloat(priceStr);
    if (isNaN(price) || price < 0) {
      toast.error('Enter a valid price');
      return false;
    }
    try {
      await axios.post(`${API}/pricesheet/entry`, { model, carrier, storage, grade, price });
      toast.success('Price saved');
      await load();
      return true;
    } catch (e) {
      toast.error('Failed to save price');
      return false;
    }
  };

  const handleAddModel = async () => {
    const name = newModelName.trim();
    if (!name) { toast.error('Enter a model name'); return; }
    if (!newModelCarriers.length || !newModelStorages.length) { toast.error('Select at least one carrier and storage size'); return; }
    // Create empty entries (price=0) for each combination — user fills in prices.
    const promises = [];
    for (const carrier of newModelCarriers) {
      for (const storage of newModelStorages) {
        for (const grade of GRADES) {
          promises.push(axios.post(`${API}/pricesheet/entry`, {
            model: name, carrier, storage, grade, price: 0
          }));
        }
      }
    }
    try {
      await Promise.all(promises);
      toast.success(`Added ${name}. Fill in prices by clicking cells.`);
      setAddModelOpen(false);
      setNewModelName('');
      await load();
    } catch (e) {
      toast.error('Failed to add model');
    }
  };

  const handleDeleteModel = async (model) => {
    if (!window.confirm(`Delete all prices for "${model}"?`)) return;
    try {
      await axios.delete(`${API}/pricesheet/model/${encodeURIComponent(model)}`);
      toast.success('Model deleted');
      await load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleExport = async () => {
    try {
      const r = await axios.get(`${API}/pricesheet/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pricesheet_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await axios.post(`${API}/pricesheet/import`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { imported, updated, total_errors } = r.data;
      toast.success(`Imported ${imported} new, updated ${updated}${total_errors ? `, ${total_errors} errors` : ''}`);
      await load();
    } catch (e) {
      toast.error('Import failed');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleWipeAndImport = () => {
    const ok = window.confirm(
      'This will DELETE every price in your current sheet, then let you pick a new CSV to import.\n\nThis cannot be undone. Continue?'
    );
    if (!ok) return;
    (async () => {
      try {
        const r = await axios.delete(`${API}/pricesheet/all`);
        toast.success(`Cleared ${r.data.entries_deleted} prices · pick the new CSV…`);
        await load();
        // Trigger file picker so the user can immediately import a fresh sheet
        wipeImportInputRef.current?.click();
      } catch (e) {
        toast.error('Failed to clear price sheet');
      }
    })();
  };

  const handleClearAll = () => {
    const ok = window.confirm(
      'Delete every price in your sheet? This wipes ALL models, carriers and grades. Cannot be undone.'
    );
    if (!ok) return;
    (async () => {
      try {
        const r = await axios.delete(`${API}/pricesheet/all`);
        toast.success(`Cleared ${r.data.entries_deleted} prices`);
        await load();
      } catch (e) {
        toast.error('Failed to clear price sheet');
      }
    })();
  };

  const getPrice = (modelGroup, carrier, storage, grade) => {
    const found = modelGroup.prices.find(
      p => p.carrier === carrier && p.storage === storage && p.grade === grade
    );
    return found;
  };

  return (
    <div className="space-y-6" data-testid="pricesheet-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl text-foreground tracking-wide">BUYER PRICE SHEET</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track what your buyer pays for each model · grade · storage · carrier
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/calculator">
            <Button variant="outline" data-testid="open-calculator-btn">
              <Calculator className="h-4 w-4 mr-2" /> Profit Calculator
            </Button>
          </Link>
          <input
            type="file" accept=".csv" ref={fileInputRef}
            className="hidden" onChange={handleImport} data-testid="import-csv-input"
          />
          <input
            type="file" accept=".csv" ref={wipeImportInputRef}
            className="hidden" onChange={handleImport} data-testid="wipe-import-csv-input"
          />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="import-csv-btn">
            <Upload className="h-4 w-4 mr-2" /> Import CSV
          </Button>
          <Button variant="outline" onClick={handleWipeAndImport} data-testid="replace-csv-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Replace with CSV
          </Button>
          <Button variant="outline" onClick={handleExport} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          {data.grouped.length > 0 && (
            <Button
              variant="outline"
              onClick={handleClearAll}
              data-testid="clear-all-prices-btn"
              className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Clear All
            </Button>
          )}
          <Button onClick={() => setAddModelOpen(true)} data-testid="add-model-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Model
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : data.grouped.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-muted-foreground mb-4">
            No models yet. Add your first phone model to start tracking buyer prices.
          </p>
          <Button onClick={() => setAddModelOpen(true)} data-testid="empty-add-model-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Your First Model
          </Button>
        </Card>
      ) : (
        <div className="space-y-8">
          {data.grouped.map(group => {
            // Derive carriers/storages present for this model
            const carriers = [...new Set(group.prices.map(p => p.carrier))]
              .sort((a, b) => CARRIERS.indexOf(a) - CARRIERS.indexOf(b));
            const storagesByCarrier = {};
            carriers.forEach(c => {
              storagesByCarrier[c] = [...new Set(
                group.prices.filter(p => p.carrier === c).map(p => p.storage)
              )].sort((a, b) => STORAGES.indexOf(a) - STORAGES.indexOf(b));
            });
            return (
              <Card key={group.model} className="overflow-hidden border-2 border-foreground/10" data-testid={`model-card-${group.model}`}>
                <div className="bg-sheet text-white px-6 py-3 flex items-center justify-between">
                  <h3 className="font-display tracking-wider text-lg">{group.model.toUpperCase()}</h3>
                  <Button
                    size="sm" variant="ghost"
                    className="text-white hover:bg-white/20"
                    onClick={() => handleDeleteModel(group.model)}
                    data-testid={`delete-model-${group.model}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold text-foreground">Carrier</th>
                        <th className="text-left px-4 py-2 font-semibold text-foreground">Storage</th>
                        {GRADES.map(g => (
                          <th key={g} className="text-right px-4 py-2 font-semibold text-foreground">
                            Grade {g}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {carriers.map(carrier =>
                        storagesByCarrier[carrier].map((storage, idx) => (
                          <tr
                            key={`${carrier}-${storage}`}
                            className="border-t border-border hover:bg-muted/30"
                          >
                            {idx === 0 && (
                              <td
                                rowSpan={storagesByCarrier[carrier].length}
                                className="px-4 py-2 font-medium text-foreground align-top"
                              >
                                {carrier}
                              </td>
                            )}
                            <td className="px-4 py-2 text-foreground">{storage}</td>
                            {GRADES.map(grade => {
                              const entry = getPrice(group, carrier, storage, grade);
                              const isEditing = editCell &&
                                editCell.model === group.model &&
                                editCell.carrier === carrier &&
                                editCell.storage === storage &&
                                editCell.grade === grade;
                              const price = entry?.price ?? 0;
                              return (
                                <td key={grade} className="px-2 py-1 text-right">
                                  {isEditing ? (
                                    <Input
                                      type="number" step="0.01"
                                      value={editValue}
                                      onChange={e => setEditValue(e.target.value)}
                                      onBlur={async () => {
                                        const ok = await savePrice(group.model, carrier, storage, grade, editValue);
                                        if (ok) setEditCell(null);
                                      }}
                                      onKeyDown={async e => {
                                        if (e.key === 'Enter') {
                                          const ok = await savePrice(group.model, carrier, storage, grade, editValue);
                                          if (ok) setEditCell(null);
                                        }
                                        if (e.key === 'Escape') setEditCell(null);
                                      }}
                                      autoFocus
                                      className="h-8 text-right w-24 ml-auto"
                                      data-testid={`price-input-${group.model}-${carrier}-${storage}-${grade}`}
                                    />
                                  ) : (
                                    <button
                                      onClick={() => {
                                        setEditCell({ model: group.model, carrier, storage, grade });
                                        setEditValue(String(price));
                                      }}
                                      className={`w-full px-2 py-1 rounded hover:bg-accent hover:text-accent-foreground transition-colors text-right ${
                                        price > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'
                                      }`}
                                      data-testid={`price-cell-${group.model}-${carrier}-${storage}-${grade}`}
                                    >
                                      {price > 0 ? `$${price.toLocaleString()}` : '—'}
                                      <Pencil className="h-3 w-3 inline-block ml-1 opacity-0 group-hover:opacity-50" />
                                    </button>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Model Dialog */}
      <Dialog open={addModelOpen} onOpenChange={setAddModelOpen}>
        <DialogContent data-testid="add-model-dialog">
          <DialogHeader>
            <DialogTitle>Add Device Model</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Model Name</label>
              <Input
                placeholder="e.g. iPhone 17 Pro, MacBook Air M4, iPad Pro"
                value={newModelName}
                onChange={e => setNewModelName(e.target.value)}
                data-testid="new-model-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Carriers</label>
              <div className="flex gap-2 flex-wrap">
                {CARRIERS.map(c => (
                  <Button
                    key={c} type="button" size="sm"
                    variant={newModelCarriers.includes(c) ? 'default' : 'outline'}
                    onClick={() => setNewModelCarriers(s =>
                      s.includes(c) ? s.filter(x => x !== c) : [...s, c]
                    )}
                    data-testid={`new-model-carrier-${c}`}
                  >{c}</Button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">Storage Sizes</label>
              <div className="flex gap-2 flex-wrap">
                {STORAGES.map(s => (
                  <Button
                    key={s} type="button" size="sm"
                    variant={newModelStorages.includes(s) ? 'default' : 'outline'}
                    onClick={() => setNewModelStorages(arr =>
                      arr.includes(s) ? arr.filter(x => x !== s) : [...arr, s]
                    )}
                    data-testid={`new-model-storage-${s}`}
                  >{s}</Button>
                ))}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Sealed, Opened and used grades will be created. Click any cell to enter prices. Use Unlocked for Wi-Fi iPads and MacBooks.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModelOpen(false)}>Cancel</Button>
            <Button onClick={handleAddModel} data-testid="confirm-add-model">Add Model</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
